'use client';

import { useRef, useState } from 'react';
import { useI18n } from 'next-localization';

import { cn, isHospitalSiteType } from '@/lib/utils';
import { useOasApp } from '@/providers/oas-app-provider';
import { NeuAccordion, NeuIcon } from '@hcahealthcaredpx/neutron-react';

import styles from '../../listing.module.css';
import { ClearButtonRef } from '../../listing.types';
import { ApplyFiltersButton } from './apply-button';
import { ClearFiltersButton } from './clear-button';
import { FacilitiesFilter } from './facilities';
import { GendersFilter } from './genders';
import { LanguagesFilter } from './languages';
import { LocationFilter } from './location';
import { NewPatientOnlineBookingFilter } from './new-patient-online-booking';
import { SearchFilter } from './search';
import { TelemedicineAvailableFilter } from './telemedicine-available';

export const Filters = ({
  coids,
  program,
  siteType,
  clearButtonRef,
}: {
  coids: string;
  program: string;
  siteType: string;
  clearButtonRef: ClearButtonRef;
}): JSX.Element => {
  const fields = useOasApp();
  const isLocation = fields?.isLocation;
  const { t } = useI18n();
  const [state, setState] = useState(true);
  const providersInputRef = useRef(null);
  const isHospital = isHospitalSiteType(siteType);

  return (
    <div className="col-sm-12 col-md-12 col-lg-4 h-auto">
      <div className={styles.filtersCol}>
        <div className={cn('d-flex justify-content-between w-100', styles.titleContainer)}>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={() => setState((p) => !p)}
              className={cn(
                'overflow-hidden p-0 d-flex align-items-center border-0 transition-reduce',
                styles.filterButton
              )}
              aria-expanded={state}
            >
              <NeuIcon size="2x" name="filter_list" />
              <span className="sr-only">{t('OAS.Listing.Filters.FiltersLabel')}</span>
            </button>
            <h4 className={cn('fw-bold', styles.filtersHeaderText)}>
              {t('OAS.Listing.Filters.Filter')}
            </h4>
          </div>
          <ClearFiltersButton providersInputRef={providersInputRef} ref={clearButtonRef} />
        </div>

        <div data-expanded={state} className={cn('transition-reduce', styles.filtersContainer)}>
          <div className="d-flex flex-column align-items-center w-100 gap-1 px-1 pb-4">
            <NeuAccordion className="w-100">
              <NeuAccordion.Item active>
                <NeuAccordion.Header>
                  <p className="pe-4 mb-0">{t('OAS.Listing.Filters.SearchHeader')}</p>
                </NeuAccordion.Header>
                <NeuAccordion.Content
                  space={0}
                  className={styles.neuAccordeonContent}
                  aria-label="Search by doctor, clinic, or condition"
                >
                  <SearchFilter
                    coids={coids}
                    locationUrlTitle={
                      isLocation && fields.locationUrlTitle ? fields.locationUrlTitle : ''
                    }
                    program={program}
                    providersInputRef={providersInputRef}
                  />
                </NeuAccordion.Content>
              </NeuAccordion.Item>
              <NeuAccordion.Item active>
                <NeuAccordion.Header>
                  <p className="pe-4 mb-0">{t('OAS.Listing.Filters.LocationHeader')}</p>
                </NeuAccordion.Header>
                <NeuAccordion.Content
                  space={0}
                  className={styles.neuAccordeonContent}
                  aria-label="Search by city or zip code"
                >
                  <LocationFilter />
                </NeuAccordion.Content>
              </NeuAccordion.Item>
              <NeuAccordion.Item>
                <NeuAccordion.Header>{t('OAS.Profile.Details.Gender')}</NeuAccordion.Header>
                <NeuAccordion.Content space={0} aria-label="Filter by gender">
                  <GendersFilter />
                </NeuAccordion.Content>
              </NeuAccordion.Item>
              <NeuAccordion.Item active>
                <NeuAccordion.Header>{t('OAS.Profile.Details.Languages')}</NeuAccordion.Header>
                <NeuAccordion.Content space={0} aria-label="Filter by languages">
                  <LanguagesFilter />
                </NeuAccordion.Content>
              </NeuAccordion.Item>

              <NewPatientOnlineBookingFilter />

              <TelemedicineAvailableFilter />

              {isHospital && !isLocation && <FacilitiesFilter />}
            </NeuAccordion>
            <ApplyFiltersButton coids={coids} program={program} siteType={siteType} />
          </div>
        </div>
      </div>
    </div>
  );
};
